import { CardIcon, MoneyTimeIcon, WalletIcon } from "icons/outline";
import { ApplePayIcon, GoogleIcon } from "icons/brand";
import { useEffect } from "react";
import { Checkbox, cn, DropdownOption, DropdownSearch } from "ui";
import SamsungPayIcon from "../../assets/images/samsungPay.svg";
import { RoutingMethods } from "../../features/routing/types/api";
import { useAnalytics } from "../../features/analytics/analyticsProvider";

type SelectPaymentMethodProps = {
  paymentMethodOptions: DropdownOption<{ method: string; label: string }>[];
  selectedMethod: [string, { method: string; label: string }];
  setSelectedMethod: (
    method: [string, { method: string; label: string } | undefined]
  ) => void;
  shouldSaveAsDefault: boolean;
  setShouldSaveAsDefault: React.Dispatch<React.SetStateAction<boolean>>;
  className?: string;
};

export default function SelectPaymentMethod({
  paymentMethodOptions,
  selectedMethod,
  setSelectedMethod,
  shouldSaveAsDefault,
  setShouldSaveAsDefault,
  className,
}: SelectPaymentMethodProps) {
  const { analytics } = useAnalytics();

  useEffect(() => {
    if (paymentMethodOptions.length === 1) {
      setSelectedMethod(paymentMethodOptions[0]);
    }
  }, [paymentMethodOptions, setSelectedMethod]);

  return (
    <DropdownSearch<{ method: string; label: string }>
      name="Method"
      label="Select rule"
      className={cn("h-auto w-full max-w-xs capitalize", className)}
      selected={selectedMethod}
      options={paymentMethodOptions}
      onSelect={(value) => {
        analytics.track("flows_routing_rule_applied", {
          method: value[0],
          set_as_default: shouldSaveAsDefault,
        });
        setSelectedMethod(value);
      }}
      withConfirmation
      ExtraFooterAction={
        <label className="mb-4 flex items-center space-x-2 text-sm font-medium text-foundations-bolder">
          <Checkbox
            checked={shouldSaveAsDefault}
            onChange={(e) => setShouldSaveAsDefault(e.target.checked)}
          />
          <span>Set as default method</span>
        </label>
      }
      displayedValueKey="label"
      renderOption={({ method, label }) => (
        <div className="flex items-center gap-2">
          {methodIcon[method as keyof typeof methodIcon]}

          <span className="capitalize">{label.toLowerCase()}</span>
        </div>
      )}
    />
  );
}

export const methodIcon: Record<RoutingMethods, JSX.Element> = {
  CARD: (
    <div className="flex h-6 w-9 items-center justify-center rounded border border-foundations-subtle text-info group-data-[highlighted=true]:text-foundations-inverse group-data-[selected=true]:text-foundations-inverse">
      <CardIcon className="h-4 w-4" />
    </div>
  ),
  APPLE_PAY: (
    <div className="flex h-6 w-9 items-center justify-center rounded border border-foundations-subtle bg-white">
      <ApplePayIcon className="h-6 text-black" />
    </div>
  ),
  SAMSUNG_PAY: (
    <div className="flex h-6 w-9 items-center justify-center rounded border border-foundations-subtle bg-white">
      <img className="h-5" src={SamsungPayIcon.src} alt="" aria-hidden="true" />
    </div>
  ),
  GOOGLE_PAY: (
    <div className="flex h-6 w-9 items-center justify-center rounded border border-foundations-subtle bg-white">
      <GoogleIcon className="h-4 text-black" />
    </div>
  ),
  BNPL: (
    <div className="flex h-6 w-9 items-center justify-center rounded border border-foundations-subtle bg-white text-pink-800">
      <MoneyTimeIcon className="h-4 " />
    </div>
  ),
  DIGITAL_WALLET: (
    <div className="flex h-6 w-9 items-center justify-center rounded border border-foundations-subtle bg-white text-green-800">
      <WalletIcon className="h-4 " />
    </div>
  ),
};
